<template>
  <booking-block
    class="booking-block--order-details"
    :editMode="editMode"
    :valid="!validForm"
    :title="translations.your_details_header"
    :subTitle="subHeader"
    @save="saveAndClose"
    @edit="$emit('edit')"
    ref="bookingblock"
  >
    <template slot="edit">
      <div class="input-fields">
        <div class="input-fields__content">
          <input-field
            :name="translations.input_firstname"
            :value="value.firstName"
            @input="
              (firstName) => {
                this.updateCustomer({
                  firstName,
                });
              }
            "
            :icon-type="
              validForm && validForm.firstName ? '' : this.checkmarkIcon
            "
            class="booking-field booking-field--firstname"
          ></input-field>
          <input-field
            :name="translations.input_lastname"
            :value="value.lastName"
            @input="
              (lastName) => {
                this.updateCustomer({
                  lastName,
                });
              }
            "
            :icon-type="
              validForm && validForm.lastName ? '' : this.checkmarkIcon
            "
            class="booking-field booking-field--lastname"
          ></input-field>
        </div>
        <msg-block type="error" v-if="errors.firstName || errors.lastName">
          <p v-if="errors.firstName"><b v-text="errors.firstName[0]"></b></p>
          <p v-if="errors.lastName"><b v-text="errors.lastName[0]"></b></p>
        </msg-block>
      </div>
      <div class="input-fields">
        <div class="input-fields__content">
          <autosuggestion
            :text="translations.input_address"
            :value="value.address"
            @input="(address) => updateCustomer({ address })"
            @updateval="onUpdateAddressValue"
            @selectitem="onAddressSelection"
            :setOnSelect="false"
            :loading="addressSugestionLoading"
            :dataList="addressSusgestionList"
            :listFilter="false"
            filter-on-object="description"
            :icon-type="
              validForm && validForm.address ? '' : this.checkmarkIcon
            "
            class="booking-field booking-field--address"
          ></autosuggestion>
        </div>
        <msg-block type="error" v-if="errors.address">
          <p v-if="errors.address"><b v-text="errors.address[0]"></b></p>
        </msg-block>
      </div>
      <div class="input-fields">
        <div class="input-fields__content">
          <input-field
            :name="translations.input_postal"
            input-type="tel"
            mask="9999"
            :value="value.zipCode"
            @input="(inp) => this.updateCustomer({ zipCode: inp.substr(0, 4) })"
            :icon-type="
              validForm && validForm.zipCode ? '' : this.checkmarkIcon
            "
            class="booking-field booking-field--zipcode"
          ></input-field>
          <input-field
            :name="translations.input_town"
            :value="value.city"
            @input="(city) => updateCustomer({ city })"
            :icon-type="validForm && validForm.city ? '' : this.checkmarkIcon"
            class="booking-field booking-field--city"
          ></input-field>
        </div>
        <msg-block type="error" v-if="errors.zipCode || errors.city">
          <p v-if="errors.zipCode"><b v-text="errors.zipCode[0]"></b></p>
          <p v-if="errors.city"><b v-text="errors.city[0]"></b></p>
        </msg-block>
      </div>
      <div class="input-fields">
        <div class="input-fields__content">
          <selector
            :value="value.countryCode"
            @input="
              (countryCode) => {
                this.updateCustomer({ countryCode });
              }
            "
            :options="countries"
            :icon-type="
              validForm && validForm.countryCode
                ? 'drop-down'
                : this.checkmarkIcon
            "
            class="booking-field booking-field--country"
          ></selector>
          <msg-block type="error" v-if="errors.countryCode">
            <p v-if="errors.countryCode">
              <b v-text="errors.countryCode[0]"></b>
            </p>
          </msg-block>
        </div>
      </div>
      <div class="input-fields">
        <div class="input-fields__header">
          <h3 v-text="translations.contact_header"></h3>
        </div>
        <div class="input-fields__content">
          <input-field
            :name="translations.input_email"
            input-type="email"
            :value="value.email"
            @input="(email) => updateCustomer({ email })"
            :icon-type="validForm && validForm.email ? '' : this.checkmarkIcon"
            class="booking-field booking-field--mail"
          ></input-field>
          <input-field
            :name="translations.input_email_confirm"
            input-type="email"
            :value="email_confirm"
            @input="(inp) => (this.email_confirm = inp)"
            :icon-type="
              validForm && validForm.email_confirm ? '' : this.checkmarkIcon
            "
            class="booking-field booking-field--mailconfirm"
          ></input-field>
        </div>
        <msg-block type="error" v-if="errors.email || errors.email_confirm">
          <p v-if="errors.email"><b v-text="errors.email[0]"></b></p>
          <p v-if="errors.email_confirm">
            <b v-text="errors.email_confirm[0]"></b>
          </p>
        </msg-block>
      </div>
      <div class="input-fields">
        <div class="input-fields__content">
          <span class="booking-field booking-field--countrycode-text">+</span>
          <phone-code-field
            :country="value.countryCode"
            :name="translations.input_countrycode"
            v-model="phone_contrycode"
            :icon-type="
              validForm && validForm.phone_contrycode ? '' : this.checkmarkIcon
            "
            class="booking-field booking-field--countrycode"
          ></phone-code-field>
          <input-field
            :name="translations.input_phone"
            input-type="tel"
            v-model="phone_number"
            :icon-type="validForm && validForm.phone ? '' : this.checkmarkIcon"
            class="booking-field booking-field--phone"
          ></input-field>
        </div>
        <msg-block type="error" v-if="errors.phone">
          <p v-if="errors.phone"><b v-text="errors.phone[0]"></b></p>
        </msg-block>
      </div>
      <div class="input-fields">
        <div class="input-fields__header">
          <h3 v-text="translations.comment_headline"></h3>
        </div>
        <div class="input-fields__content">
          <input-field
            :name="translations.input_comment"
            :value="comment"
            @input="(val) => updateComment(val)"
            input-type="textarea"
            class="booking-field booking-field--comment"
          ></input-field>
        </div>
      </div>
      <div class="input-fields">
        <div class="input-fields__header">
          <h3 v-text="translations.voucher_headline"></h3>
        </div>
        <div class="input-fields__content">
          <input-field
            input-type="text"
            class="booking-field booking-field--voucher"
            :class="{'booking-field--voucher--success': validForm && validForm.voucher_code}"
            v-model="voucher_code"
            :icon-type="validForm && validForm.voucher_code ? '' : this.checkmarkIcon"
          ></input-field>
        </div>
        <msg-block type="error" v-if="errors.voucher_code">
          <p v-if="errors.voucher_code"><b v-text="errors.voucher_code[0]"></b></p>
        </msg-block>
      </div>
      <GmapMap ref="map" :center="{ lat: 10, lng: 10 }" v-show="false" />
    </template>
    <template slot="view">
      <table class="table">
        <tbody>
          <tr>
            <td v-text="translations.input_firstname"></td>
            <td v-text="value.firstName"></td>
          </tr>
          <tr>
            <td v-text="translations.input_lastname"></td>
            <td v-text="value.lastName"></td>
          </tr>
          <tr>
            <td v-text="translations.input_address"></td>
            <td v-text="value.address"></td>
          </tr>
          <tr>
            <td v-text="translations.input_postal"></td>
            <td v-text="value.zipCode"></td>
          </tr>
          <tr>
            <td v-text="translations.input_town"></td>
            <td v-text="value.city"></td>
          </tr>
          <tr>
            <td v-text="translations.input_country"></td>
            <td v-text="selectedContry ? selectedContry.text : ''"></td>
          </tr>
          <tr>
            <td v-text="translations.input_email"></td>
            <td v-text="value.email"></td>
          </tr>
          <tr>
            <td v-text="translations.input_phone"></td>
            <td v-text="value.phone"></td>
          </tr>
          <tr v-if="comment">
            <td v-text="translations.comment_headline"></td>
            <td v-text="comment"></td>
          </tr>
          <tr v-if="validOfferCode">
            <td v-text="translations.voucher_headline"></td>
            <td v-text="voucher_code"></td>
          </tr>
          <tr>
            <td v-text="translations.input_newsletter"></td>
            <td>{{ (newsletter) ? translations.newsletter_yes : translations.newsletter_no }}</td>
          </tr>
        </tbody>
      </table>
    </template>
    <div class="booking-newsletter" slot="footer" v-if="editMode">
      <checkbox
        :name="translations.input_newsletter"
        :value="newsletter"
        @checked="(val) => updateNesletterSetting(val)"
      ></checkbox>
      <p
        class="booking-newsletter__text"
        v-text="translations.newsletter_text"
      ></p>
    </div>
  </booking-block>
</template>

<script>
import InputField from "../input-field.vue";
import Selector from "../selector.vue";
import Checkbox from "../checkbox.vue";
import Validate from "validate.js";
import MsgBlock from "../msg-block.vue";
import _ from "underscore";
import SkovApi from "./../../skovapi.js";
import { gmapApi } from "vue2-google-maps";
import Autosuggestion from "./../autosuggestion.vue";
import PhoneCodeField from "./../phone-code-field.vue";
import BookingBlock from "./booking-block.vue";

const ValidOfferCodes = ['250', '200', '300'];

export default {
  props: {
    editMode: { type: Boolean, default: true },
    comment: { type: String, default: "" },
    newsletter: { type: Boolean, default: true },
    value: {
      type: Object,
      default() {
        return {
          firstName: "",
          lastName: "",
          address: "",
          zipCode: "",
          city: "",
          countryCode: "DK",
          email: "",
          phone: "",
        };
      },
    },
  },
  data() {
    return {
      checkmarkIcon: "checkmark",
      translations: APP_CONFIG.TRANSLATION.BOOKING_ORDER_DETAILS,
      phone_number: "",
      phone_contrycode: "45",
      email_confirm: "",
      voucher_code: '',
      errors: {},
      showErrors: false,
      addressSusgestionList: [],
      addressSugestionLoading: false,
    };
  },
  mounted() {
    this.readValuePhone();
  },
  methods: {
    onAddressSelection({ value }) {
      if (!this.google || !this.$refs.map) {
        return;
      }

      this.$refs.map.$mapPromise.then((map) => {
        let placeService = new this.google.maps.places.PlacesService(map);
        placeService.getDetails(
          {
            placeId: value.place_id,
            fields: ["address_components"],
          },
          (place, status) => {
            if (status != this.google.maps.places.PlacesServiceStatus.OK) {
              return;
            }

            if (place.address_components.length) {
              let street_number = place.address_components.filter(
                ({ types }) => types.indexOf("street_number") > -1
              );
              let route = place.address_components.filter(
                ({ types }) => types.indexOf("route") > -1
              );
              let town = place.address_components.filter(
                ({ types }) => types.indexOf("sublocality") > -1
              );
              let country = place.address_components.filter(
                ({ types }) => types.indexOf("country") > -1
              );
              let postal_code = place.address_components.filter(
                ({ types }) => types.indexOf("postal_code") > -1
              );

              let customerData = {};

              if (route.length) {
                //Set customer address detils
                let address = route[0].long_name;
                if (street_number.length) {
                  address += " " + street_number[0].long_name;
                }

                customerData.address = address;
              }

              if (town.length) {
                customerData.city = town[0].long_name;
              }

              if (country.length) {
                customerData.countryCode = country[0].short_name;
              }

              if (postal_code.length) {
                customerData.zipCode = postal_code[0].long_name;
              }

              if(Object.keys(customerData).length) {
                this.$nextTick(() => {
                  this.updateCustomer(customerData);
                });
              }
            }
          }
        );
      });
    },
    onUpdateAddressValue(value) {
      if (!this.autocompleteService) {
        return;
      }

      this.addressSugestionLoading = true;
      this.autocompleteService
        .getPlacePredictions({
          input: value,
          types: ["address"],
          language: "da",
          componentRestrictions: { country: "dk" },
        })
        .then(({ predictions }) => {
          this.addressSugestionLoading = false;
          this.addressSusgestionList = predictions;
        })
        .catch((err) => {
          console.error(err);
        });
    },
    updateCustomer(data) {
      this.$emit("input", Object.assign({}, this.value, data));
    },
    updateComment(comment) {
      this.$emit("comment", comment);
    },
    updateNesletterSetting(value) {
      this.$emit("newsletter", value ? true : false);
    },
    readValuePhone() {
      if (this.value.phone) {
        this.phone_contrycode = this.value.phone.substr(1, 2);
        this.phone_number = this.value.phone.substr(3);
      }
    },
    saveAndClose() {
      if (this.validForm === null) {
        this.hideValidationErrors();
        this.$nextTick(() => {
          this.$refs.bookingblock.close();
        });
      } else {
        this.showValidationErrors();
      }
    },
    showValidationErrors() {
      this.showErrors = true;
      this.updateErrors();
    },
    hideValidationErrors() {
      this.showErrors = false;
      this.errors = {};
    },
    updateErrors() {
      this.errors = Object.assign({}, this.validForm);
    },
  },
  watch: {
    currentUserDetails() {
      this.setUserDetails();
    },
    phoneNumber() {
      this.updateCustomer({
        phone: this.phoneNumber,
      });
    },
    "value.phone"() {
      this.readValuePhone();
    },
    editMode() {
      if (this.editMode) {
        this.readValuePhone();
        this.email_confirm = this.value.email;
      }
    },
    "value.zipCode"() {
      if (this.value.zipCode && this.value.zipCode.length === 4) {
        SkovApi.getTownByZip(this.value.zipCode).then((city) => {
          if (city) {
            this.updateCustomer({
              city,
            });
          }
        });
      }
    },
    validForm() {
      if (this.showErrors) {
        this.updateErrors();
      }

      this.$emit("valid", !this.validForm ? true : false);
    },
    validOfferCode() {
      this.$emit('validOfferCode', (this.validOfferCode) ? this.voucher_code : '');
    }
  },
  computed: {
    google: gmapApi,
    autocompleteService() {
      return this.google
        ? new this.google.maps.places.AutocompleteService()
        : null;
    },
    phoneNumber() {
      return (
        "+" + this.phone_contrycode.toString() + this.phone_number.toString()
      );
    },
    countries() {
      return [
        {
          text: this.translations.input_country,
          value: " ",
        },
      ].concat(
        this.$store.state.countries.map((country) => {
          return {
            text: country.name,
            value: country.code,
          };
        })
      );
    },
    selectedContry() {
      let selectedCountry = this.countries.filter(
        ({ value }) => value == this.value.countryCode
      );
      return selectedCountry.length ? selectedCountry[0] : null;
    },
    subHeader() {
      let str = "";

      if (this.validForm === null && this.value.firstName) {
        str = this.value.firstName;

        if (this.value.lastName) {
          str += " " + this.value.lastName;
        }

        return str;
      }

      return this.translations.sub_header;
    },
    validForm() {
      let errors = {};
      errors = Object.assign(
        errors,
        Validate(
          this.value,
          {
            firstName: {
              presence: {
                allowEmpty: false,
                message: this.translations.ERRORS.firstName,
              },
            },
            lastName: {
              presence: {
                allowEmpty: false,
                message: this.translations.ERRORS.lastName,
              },
            },
            address: {
              presence: {
                allowEmpty: false,
                message: this.translations.ERRORS.address,
              },
            },
            zipCode: {
              presence: {
                allowEmpty: false,
                message: this.translations.ERRORS.zipCode,
              },
              numericality: {
                message: this.translations.ERRORS.zipCode,
              },
              length: {
                is: 4,
                message: this.translations.ERRORS.zipCode_tooshort,
              },
            },
            city: {
              presence: {
                allowEmpty: false,
                message: this.translations.ERRORS.city,
              },
            },
            countryCode: {
              presence: {
                allowEmpty: false,
                message: this.translations.ERRORS.countryCode,
              },
            },
            email: {
              presence: {
                allowEmpty: false,
                message: this.translations.ERRORS.email,
              },
              email: {
                message: this.translations.ERRORS.email_not_valid,
              },
            },
            phone: {
              presence: {
                allowEmpty: false,
                message: this.translations.ERRORS.phone,
              },
              format: {
                pattern: /^\+[0-9]{9,10}$/,
                flags: "m",
                message: this.translations.ERRORS.phone_format,
              },
            },
          },
          {
            fullMessages: false,
          }
        )
      );

      if (this.phone_contrycode.length !== 2) {
        errors.phone_contrycode = [this.translations.ERRORS.phone_format];
      }

      if (!this.email_confirm && this.editMode) {
        errors.email_confirm = [this.translations.ERRORS.email_confirm];
      }

      if (this.value.email != this.email_confirm && this.editMode) {
        errors.email_confirm = [this.translations.ERRORS.email_not_equal];
      }

      if(this.voucher_code && !this.validOfferCode) {
        errors.voucher_code = [this.translations.ERRORS.not_valid_voucher_code];
      }

      return !_.isEmpty(errors) ? errors : null;
    },
    validOfferCode() {
      return ValidOfferCodes.indexOf(this.voucher_code.trim()) > -1 ? true : false;
    }
  },
  components: {
    InputField,
    Selector,
    Checkbox,
    MsgBlock,
    Autosuggestion,
    PhoneCodeField,
    BookingBlock,
  },
};
</script>